import { FeatureFlagsHelper } from './FeatureFlagsHelper';
import { TagRequest } from './ServerApi/requests/TagRequest';

/**
 * There are two types of "feature flags": Development-only flags, and permanent features.
 *
 * "Development" feature flags (features that are being built, for which the flags will be
 * removed once the feature is shipped) belong in this file.
 * ( more info here: https://mytableau.tableaucorp.com/display/devcomm/VizPortal+Client+-+Feature+Flags )
 *
 * Flags for permanent features should not live here; instead, those flags should live on the server
 * or site objects of ISessionInfo, depending on whether they can be toggled at the server or site
 * level.

 One day this will move from a guideline to a requirement and you will get an urgent bug to
 move your flag, if it falls into that category.
 *
 * (TFS 653138 - Move permanent feature flags out of IFeatureFlags and into server/site info)
 *
 * This module is a global object that is as low-level in the app as possible,
 * so we can theoretically put any parameter of the application behind a flag. Flags are specified
 * here, but can be overridden at runtime via URL query parameters.
 */
export module FeatureFlags {

  /**
   * Allows direct assignment of a remote query agent to a data source
   * Created: 2017-03-16 bjudkins
   */
  // remove: TFS 808766
  export const bridgeLiveDirectAssignEnabled = false;

  /**
   * Enables Admin Viz tab of bridge extract refresh
   * Created: 2018-09-18 xyao
   */
  // remove: TFS 692209
  export const bridgeExtractRefreshAdminVizEnabled = false;

  /**
   * Enables showing bridge client version on Bridge settings page
   * Created: 2018-10-08 fuliu
   */
  // remove: TFS 795501
  export const bridgeShowClientVersionEnabled = false;

  /**
   * Enables sending warning emails to customers if remote extract refresh is not completed on time
   * Created: 2019-03-25 fuliu
   */
  // remove: TFS 889404
  export const bridgeSendWarningForExtractRefresh = false;

  /**
  *  Enables the feature where user can use their OAuth credential to refresh WDC data souces.
  */
  // Created: 2015-10-16 wechen
  // Enabled: 2016-01-07 wechen
  // Disabled: 2016-02-09 mbgumport
  // remove: TFS 808766
  export const oauthWDCServerRefreshEnabled = false;

  // Enables creation of self service schedules
  // Created: 2016-10-24 dcoonan
  // remove: TFS 808776
  export const selfServiceSchedulesEnabled = false;

  // Enables the feature to create user time zone
  // Created: 2017-03-20 nchenglie
  export const userTimeZoneEnabled = false;

  /**
  * Enables the job management feature.
  */
  // Created: 2018-09-19 nchenglie
  export const jobManagementEnabled = false;

  /*
   * Shows the Certification information on the DS place page.
   */
  // Created: 2017-04-18 eprescott
  // remove: TFS 808701
  export const datasourceCertificationEnabled = true;

  /*
   * Shows a link to disk space usage admin view if enabled
   */
  // Created: 2016-01-11 jchar
  export const lowDiskSpaceEnabled = false;

  /*
   * Display Tableau desktop license usage summary if enabled
   */
  // Created: 2016-03-31 jili
  // Enabled: 2016-04-04 jili
  // remove: TFS 808741
  export const desktopReportingEnabled = true;

  /*
   * Allows subscriptions to be triggered based off of a threshold condition.
   */
  // Created: 2016-06-08 cpredmore
  // Enabled: 2016-09-29 cpredmore
  // Disabled: 2016-10-28 abrandon (TFS: 581467)
  // Enabled: 2017-01-24 tyakushev
  // remove: TFS 808745
  export const alertOnThresholdConditionEnabled = true;

  /*
   * Allows users to resume suspended Data Alerts.
   */
  // Created: 2018-09-25 hmughnetsyan
  // Enabled: 2019-01-14 osibiryov
  export const suspendDataAlerts = true;

  /*
   * Display the data alert privacy info if enabled
   */
  // Created: 2018-12-12 zszugyi
  // Enabled: 2019-01-14 kknightboehm
  export const displayDataAlertPrivacy = true;

  /*
   * Allows viewing Metrics by proxying out to an external Metrics Server.
   */
  // Created: 2017-06-12 cpredmore
  export const metricsProxyEnabled = false;

  /*
   * Allows navigating to Metrics Authoring
   */
  // Created: 2018-11-13 lascher
  export const metricsAuthoringPathEnabled = false;

  /*
   * Enables Metrics Space Navigation
   */
  // Created: 2019-02-02 alisanti
  export const metricsSpaceEnabled = false;

  /*
   * Enables Metrics Data Quality Warnings
   */
  // Created: 2020-07-16 jukelly
  export const metricsDataQualityWarningsEnabled = false;

  /*
   * Allow UI actions for remove self from data alert
   */
  // Created: 2016-10-19 jfitzgerald
  // Enabled: 2016-11-30 abrandon
  // remove: TFS 775869
  export const removeSelfFromDataAlertEnabled = true;

  /*
   * Allow UI actions for adding self to data alert
   */
  // Created: 2016-10-19 jfitzgerald
  // Enabled: 2016-11-30 abrandon
  // remove: TFS 775869
  export const addSelfToDataAlertEnabled = true;

  /*
   * Allows Tableau support users to be added to a Tableau Online site.
   */
  export const addSupportUserEnabled = false;

  /**
   * Enabled Guest Account Per Site Functionality
   */
  //Created: 2016-08-22 preguraman
  //Enabled: 2016-09-26 preguraman
  // remove: TFS 762916
  export const guestAccountPerSiteEnabled = true;

  /**
   * Allows the server admin to enable the cache warmup feature
   *
  */
  // Created: 2016-09-23 gfilpus
  // Enabled: 2017-03-20 gfilpus
  // remove: TFS 774533
  export const cacheWarmup = true;

  /**
   * Enable "Refresh Now" menu item for remotely-refresh data sources
   * Created: 2016-12-08 bjudkins
   */
  // remove: 808766
  export const dataSyncRefreshNowEnabled = false;

  /**
   * Uses a new, vizportal-based getting started screen, rather than rendering a marketing page.
   * Created: 2016-09-29 cshiraishi
   * Enabled: 2016-11-22 cshiraishi
   */
  // Remove: TFS 775870
  export const newGettingStartedScreen = true;

  /**
   * Shows a custom banner for opening in app for mobile devices on the login page.
   */
  // Created: 2017-03-15
  // Enabled: 2017-03-17
  // remove: TFS 808698
  export const customAppBanner = true;

  /**
   * Shows 'what's new' menu option
   */
  // Created: 2017-04-06 klin
  export const whatIsNewMenuOptionEnabled = false;

  /**
   * Enable renaming views on server (without going into web authoring)
   */
  // Created: 2017-08-07 janlee
  export const renameViewsEnabled = false;

  /**
   * Show hyper instead of dataengine.
   */
  //Created: 2017-05-8 mroberts
  // remove: TFS 808750
  export const hyperOnServerEnabled = false;

  /**
   * Flag to know if new tabadmin services (TSM) feature is enabled (backing feature flag is TabadminService)
   */
  // Created: 2017-05-18 acarre
  // remove: TFS 808750
  export const tableauServicesManagerEnabled = false;

  /**
   * Display data connection tooltip in viz view.
   */
  // Created: 2017-08-16 shubbard
  export const dataConnectionTooltipInVizView = true;

  /**
   * Send analytics info to server
   */
  // Created: 2017-11-21 jfitzgerald
  export const OnlineAnalyticsPipelineEnabled = false;

  /**
   * Display SCIM settings in the authentication tab
   * See: http://l/tfs/697361
   */
  // Created: 2018-02-21 mvafadost
  export const scimApiTokenEnabled = false;

  /**
   *  Show new site settings tab for extensions
   */
  // created: 2018-4-4 jfitzgerald
  export const extensionsSettingsTabEnabled = true;

  /**
   *  Enable/Disable Mentions in Commenting
   */
  // Created: 2018-4-30 nranganna
  export const commentingMentionsEnabled = true;

  /**
   * Show all the data catalog features
   */
  // created: 2018-5-4 dbecker
  // enabled: 2019-05-13 abrandon
  export const metadataAreaEnabled = true;

  /**
   * UI flag that controls setting database and
   * table contacts
   */
  // created: 2019-07-01 rlemma
  export const externalContentContactEnabled = true;

  /**
   * Enable/Disable remove external content action
   */
  export const removeExternalContentEnabled = true;

  /**
   *  Permission action for datacatalog content, e.g. database and table.
   */
  // created: 2019-04-19 mechen
  export const dataCatalogPermissionEnabled = true;

  /**
   * Enable/Disable severe data quality warnings
   */
  export const catalogSevereWarningsEnabled = true;

  /**
   * Enable/Disable external content tags
   */
  export const catalogTagsEnabled = true;

  /**
   * Enable/Disable data quality warnings site admin viz
   */
  export const dqwAuditVizEnabled = true;

  /**
   * Show suspend subscription status and allow users to resume suspended subscriptions
   */
  // Created: 2018-09-11 jvanduyne
  // Enabled: 2018-10-29 jvanduyne
  export const suspendSubscriptions = true;

  /**
   * Flow overview page and run history update every minute
   */
  // Created: 2018-09-07 jsigo
  export const autoUpdateFlowGrids = true;

  /**
   * Show suspended flow task status and allow users to resume suspended flows
   */
  // Created const 2018-10-17 jsigo
  export const autoSuspendFlowTasksEnabled = false;

  /**
   * Define policies that all new passwords must comply with.
   */
  // Created: 2018-10-31 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const passwordPoliciesEnabled = true;

  /**
   * TFS852547 - Sheets as Tabs Dashboards should preview like Phoenix
   * Mobile Web shows these dashboards with tabs.  Phoenix shows them without tabs.
   */
  // Created: 2018-12-06 majung
  export const SheetsAsTabsDashboardsPreviewLikePhoenix = true;

  /**
   * Make the password expire after a certain amount of days and force the user to reset it.
   */
  // Created: 2018-11-16 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthPasswordExpirationEnabled = true;

  /**
   * Block the user after many failed login attempts and limit the rate of consecutive failed attempts.
   */
  // Created: 2018-12-19 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthLoginRateLimitingEnabled = true;

  /**
   * Allow the user to request RefreshTokens to use in API automation.
   */
  // Created: 2019-04-04 fpagliaricci
  export const personalAccessTokensEnabled = false;

  /**
   * Show a page of recommended vizzes.
   */
  // Created: 2018-12-20 dsimpson
  // Enabled: 2019-06-17 xmao
  export const vizRecommendationsEnabled = true;

  /**
   * Sets the Home Landing Experience as the start page
   */
  // Created: 2019-01-16 jwonglam
  export const homePage = true;

  /**
   * Allows admins to set a default start page at the site level
   */
  // Created: 2019-01-28 jwonglam
  export const siteDefaultStartPage = true;

  /**
   * Enables functionality pertaining to PDF Subscriptions SP1
   */
  // Created: 2019-01-31 edwang
  export const pdfsubscriptionsSP1Enabled = true;

  /**
   * Enables components and UI pertaining to PDF Subscriptions SP1
   */
  // Created: 2019-04-18 toshiro
  export const pdfsubscriptionsUIEnabled = true;

  /*
   * Display Tableau IBA license usage summary if enabled
   */
  // Created: 2019-03-01 antsmith
  export const ibaReportingEnabled = false;

  /**
   * Enable/Disable the the named Sharing feature
   * This feature is gated by FF and site setting and so this flag will only be directly used in site setting
   * For everything else use Serverservice.isNamedSharingEnabled()
   * Created: 2019-02-27 sanaik
   * Enabled: 2019-08-02 saramirez
   */
  export const namedSharingEnabled = true;

  /**
   * Shows the mobile biometrics setting
   */
  // created: 2019-03-18 mmathews
  export const showMobileBiometricsSetting = false;

  /**
   * Shows Extract Move To The Web flag
   */
  // created: 2019-03-26 biliu
  export const extractMoveToTheWebEnabled = false;

  /*
  * Share dialog in Vizportal Client for sharing view, workbook, project, datasource
  * Created: 2019-04-11 rkandhal
  * Enabled: 2019-08-02 saramirez
  */
  export const newShareDialogEnabled = true;

  /**
   * Feature flag for flow web edit
   */
  // Created: 2019-04-15 anlin
  export const MaestroFlowEditingServerV1 = false;

  /*
  * Enables the sign in note
  */
  // Created: 2019-04-17 rjoshi
  export const signInNoteEnabled = false;

  /**
   * Enables AFE controls in the settings page
   */
  export const alternativeFederationEngineUserInterface = false;

  /**
   * Enables EAR for flows
   */
  // Created: 2019-05-10 vhalitsyn
  export const maestroEarEnabled = false;

  /**
   * Welcome Banner Server Customization
   */
  // Created: 2019-06-19 jwonglam
  export const serverWelcomeChannelEnabled = false;

  /**
   * Editing experience for data roles
   */
  // Created: 2019-06-20 csachse
  export const semanticsServiceDataRoleEditingEnabled = true;

  /*
   * Enables Mark Animation on server client
   */
  // Created: 2019-06-12 tyoon
  // Enabled: 2019-11-01 tyoon
  export const markAnimationServer = true;

  /**
   * Enable the User Visibiilty feature
   */
  // Created: 2019-06-25 sfan
  export const userVisibilityEnabled = false;

  /**
   * Request Access feature flag
   */
  // Created: 2019-06-19 ayavnilovitch
  export const requestAccessEnabled = false;

  /**
   * Enables Subscriptions on Extract Refreshes
   */
  // Created: 2019-07-09 dmaceda
  // Enabled: 2020-02-18 cnannivalat
  export const subscriptionsOnExtractRefreshEnabled = true;

  /**
   * Enables Failure Propagation for Subscriptions on Extract Refresh
   */
  // Created: 2019-09-10 brocklin
  // Enabled: 2020-02-18 cnannivalat
  export const subscriptionsOnExtractRefreshFailurePropagationEnabled = true;

  /**
   * Created: 2019-07-03 odangat
   */
  export const setCertificationEnabled = true;

  /**
   * Favorite metrics through CRASS
   */
  export const crassFavorites = false;

  /**
   * Enable the all content types tab in search results, powered by crass.
   * Created 2019-09-6 vrobinweiler, #dev-voltron
   */
  export const crassSearch = false;

  /**
   * Show connected objects for dataroles
   */
  // Created: 2019-08-02 csachse
  export const semanticsServiceDataRoleConnectionsEnabled = false;

  /*
   * Enable/Disable the 19.4 UI elements for Job Management
   */
  // Created: 2019-07-24 arama
  export const jobManagement194UIEnabled = false;

  /**
   * Enable/Disbale Managing the ability to create "Run Now" jobs
   */
  // Created: 2019-08-13 arama
  export const enableRunNowEnabled = false;

  /**
   * Metrics content type site setting
   */
  export const metricsContentTypeSiteSettingEnabled = false;

  /**
 * Web Zone Enabled site setting
 */
  export const webZonesSiteSettingEnabled = true;

  /*
   * Enables last modified by user for workbook and datasource
   */
  //Created: 2019-09-19 sadhikari
  export const modifiedByUserEnabled = false;

  /**
   * Allow Server Side Refresh via Live Connection to Bridge client when refreshing extracts
   * Created: 2019-09-27 fuliu
   */
  // remove: TFS 986397
  export const bridgeServerSideRefresh = false;

  /**
   * Allow Server Triggered Client Side Refresh by Bridge client when refreshing extracts
   * Created: 2019-12-23 fuliu
   */
  // remove: TFS 1049605
  export const bridgeServerTriggeredClientSideRefresh = false;

  /**
   * Enable locking subprojects feature
   */
  // Created: 2019-09-30 jvanduyne
  export const lockingSubProjectsEnabled = false;

  /**
   * Enables creating workbooks from External Assets (catalog)
   * Created: 2019-10-09 lpillar
   */
  export const newWorkbookFromCatalog = true;

  /**
   * Enables using the SSS dialog to edit admin schedules
   */
  // Created: 2019-10-16 arama
  export const adminSelfServiceSchedulesEnabled = false;

  /**
   * Deep link to mobile app.
   * On mobile devices, redirects from the browser to Tableau Mobile app.
   */
  // Created: 2019-10-11 dsingh
  // Enabled: 2019-10-30
  // remove: TFS 1022799
  export const enableDeepLinking = true;

  /**
   * Enables multi-select in filter by content type menu
   * Created: 2019-11-4 lmardoum
   * Enabled: 2019-12-17
   */
  export const contentTypeMenuUpdate = true;

  /**
   * Enables viewing and editing value synonyms for data roles
   */
  // Created: 2019-10-28 csachse
  export const dataRolesEditValueSynonymsEnabled = false;

  /**
   * Enables incremental refresh
   */
  // Created: 2019-10-29 hchen
  export const incrementalRefresh = true;

  /**
   * Enables site quota feature
   */
  // Created: 2019-10-30 sanaik
  export const siteQuotaEnabled = false;

  /**
   * Enables external services site level settings.
   */
  // Created: 2019-11-21 jnegara
  export const extSvcSiteLevelSettingsEnabled = false;

  /**
   * Enables collection features
   */
  // Created: 2019-12-02 bmoulin
  export const collectionsEnabled = false;

  /**
   * Enables favoriting a collection
   */
  // Created: 2020-05-08 snuriel
  export const useCollectionsFavorites = false; // DONT MERGE IN TRUE

  /**
   * Enables share collection
   */
  // Created: 2020-04-14 bmoulin
  export const shareCollectionsEnabled = false;

  /**
   * Enables shared with me for collection
   */
  // Created: 2020-10-15 rkandhal
  export const sharedWithMeCollectionsEnabled = false;

  /**
   * Enables published connection features
   */
  // Created: 2019-11-19 phan
  export const publishedConnectionEditorEnabled = false;

  /**
   * Enables starters channel in home page
   */
  // Created: 2019-12-02 oattoun
  export const startersChannelEnabled = false;

  /**
   * Enables Upload Workbook feature from the Creat content button
   */
  // Created: 2020-01-03 jwonglam
  export const uploadWorkbookFromWebEnabled = false;

  /**
   * Enables Merge/Dedupe feature in Tableau Catalog (Data Catalog)
   */
  // Created: 2020-01-08 jweill
  export const mergeDedupeDatabasesEnabled = false;

  /**
   * Enables published connection content management ship-point 1
   */
  // Created: 2020-01-15 achristian
  export const publishedConnectionContentManagementEnabled = false;

  /**
   * Enables drag-and-drop reordering for favorites
   */
  // Created: 2020-01-17 mswiger
  export const reorderFavoritesEnabled = false;

  /**
   * Displays a confirm dialog when reordering favorites on non custom sort
   */
  // Created: 2020-02-12 bmoulin
  export const confirmReorderFavorites = true;

  /**
  * Enables license upon login option
  */
  // Created: 2020-01-24 rjoshi
  export const licenseUponLoginEnabled = false;

  /**
   * Enables Server Overview Admin View
   */
  // Created: 2020-01-23 sanaik
  export const serverOverviewAdminViewEnabled = false;

  /**
   * Enables Stale Content Admin View
   */
  // Created: 2020-01-23 sanaik
  export const staleContentAdminViewEnabled = true;

  /**
   * Enables Stale Content Admin View Extension
   */
  // Created: 2020-04-21 aolson
  export const staleContentExtensionEnabled = false;

  /**
   * Enables visibility of custom views that are connected to the workbook
   */
  //Created: 2020-02-13 jbouvier
  export const showCustomViewsForWorkbookEnabled = true;

  /**
   * Allows the site admin to set a default site time zone.
   */
  // Created: 2020-03-09 achristian
  export const siteTimeZoneEnabled = true;

  /**
   * Enables Shared with me - basic functionality
   */
  // Created: 2020-03-17 rkandhal
  // Enabled: 2020-05-22 jvanduyne
  export const sharedWithMeBasicEnabled = true;

  /**
   * Enables Shared with me - hide shared content support
   */
  // Created: 2020-03-17 rkandhal
  export const hideSharedContentEnabled = false;

  /**
   * Enables Shared with me - custom views support
   */
  // Created: 2020-03-17 rkandhal
  // Enabled: 2020-05-22 jvanduyne
  export const sharedWithMeForCustomViewEnabled = true;

  /**
   * Enables Shared with me - group sharing support
   */
  // Created: 2020-03-17 rkandhal
  export const groupSharingEnabled = false;

  /**
   * Enables flow drafts
   */
  // Created: 2020-03-30 jsigo
  export const flowDraftsEnabled = false;

  /*
   * Enables the use of Self Service Schedules for Extract Refreshes
   */
  // Created 2020-03-23 arama
  export const selfServiceSchedulesForRefreshEnabled = false;

  /*
   * Enables Workbook Acceleration UI
   */
  // Created 2020-04-15 dguo
  export const workbookAccelerationUIEnabled = false;

  /**
   * Enables the new react implementation of the settings page
   */
  export const settingsReactMigration = true;

  /**
   * Enables the react implementation of the settings coming in wave 2
   */
  export const settingsReactMigration2 = false;

  /**
   * Enables Redesigned Analytics Extensions UI implemented in 2020.3
   */
  // Created 2020-05-05 rxing
  export const anExtRedesignV20203Enabled = false;

  /**
   * Enables enabling/disabling extract refresh auto-suspend feature
   */
  // Created: 2020-05-26 ymuntyan
  export const extractRefreshPauseEnabled = false;

  /**
   * Enables notification center
   */
  // Created: 2020-06-08 cmei
  export const notificationCenterEnabled = false;

  /**
   * Feature flag for the Einstein Discovery Integration
   */
  // Created: 2020-06-18 kjacyna
  export const einsteinDiscoveryPredictionsAnalyticsExtension = false;

  /**
   * Enables Ask Data Usage Dashboard
   */
  // Created: 2020-06-22 dhruv.khanna
  export const askDataUsageDashboardEnabled = false;

  /**
   * Enables/Diasbles personal space
   */
  // Created: 2020-06-23 sanaik
  export const personalSpaceEnabled = false;

  /**
   * Enables auto save, mainly used for workbook drafts
   */
  // Created: 2020-06-24 oattoun
  export const webAuthoringAutoSaveEnabled = false;

  /**
   * Enables Ask Data Usage and Performance Dashboards for Server Admins
   */
  // Created: 2020-06-22 dhruv.khanna
  export const askDataPerformanceDashboardEnabled = false;

  /**
   * Enables NLPViewerEnablement
   */
  // Created: 2020-07-17 tsmirnov
  export const nlpviewerEnablementEnabled = false;

  /**
   * Allow metric to auto-update without having to manually refresh
   */
  // Created: 2020-06-08 cschaden
  export const metricsAutoUpdateEnabled = false;

  /**
   * Enable the React Omnibox component, meanwhile Angular Omnibox will be disabled.
   */
  // Created: 2020-08-07 llin
  export const reactOmniboxEnabled = false;

  /**
   * Enable copying embed code from share dialog for metrics
   */
  // Created: 2020-08-03 sruthi.pillai
  export const metricsEmbeddingEnabled = false;

  /**
   * Enables metrics integration in catalog
   *
   * Created: 2020-08-04 cbutcher
   */
  export const metricsDataCatalogEnabled = false;

  /**
   * Enables hiding all entry points to web authoring on mobile
   */
  //Created: 2020-08-12 sdietzel
  export const killMobileAuthoring = false;

  /**
   * Flag to use CRASS for global search of databases and tables
   */
  // Created: 2020-08-07 cchudasama
  export const searchWithCrassDatabasesAndTables = false;

  /**
   * Returns the new view model with project containing the content information
   */
  //Created: 2020-08-26 sanaik
  export const refactorProjectEnabled = false;

  /**
   * DQW Triggers feature by the Data Catalog team: users can set up automatic data quality warnings
   * when extract refreshes fail.
   *
   * This flag is linked to the backend.
   */
  // Created: 2020-09-04 abrandon
  export const dqwTriggersEnabled = false;

  /**
   * Enables the dismissal of Post-Login notification
   */
  // Created: 2020-09-11 qzhuang
  export const dismissPostLoginNotificationEnabled = true;

}

// Override the feature flags as soon as possible after the page loads and the feature flag defaults
// are set, before the application is initialized. The helper must be invoked after declaration of
// the feature flags, otherwise there will not be existing values to overwrite
if (window.location.search) {
  FeatureFlagsHelper.overwriteFeatureFlagsFromQueryString(window.location.search, FeatureFlags);
}
